import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import apiClient from '@/services/api'
import { useToast } from '@/hooks/useToast'

import { useErrorHandler } from './useErrorHandler'

interface ForgotPasswordRequest {
  email: string
}

interface ForgotPasswordResponse {
  message: string
  reset_token: string
}

interface UseForgotPasswordState {
  data: Nullable<ForgotPasswordResponse>
  isLoading: boolean
  error: Nullable<string>
  isSuccess: boolean
  emailSent: boolean
  submittedEmail: string
  resetToken: string
}

interface UseForgotPasswordReturn extends UseForgotPasswordState {
  handleSubmit: (email: string) => Promise<void>
  handleResend: () => Promise<void>
  reset: () => void
}

/**
 * Custom hook for managing forgot password state
 * Handles sending password reset email
 */
export function useForgotPassword(): UseForgotPasswordReturn {
  const { t } = useTranslation()
  const { handleError } = useErrorHandler()
  const { toast } = useToast()
  const [state, setState] = useState<UseForgotPasswordState>({
    data: null,
    isLoading: false,
    error: null,
    isSuccess: false,
    emailSent: false,
    submittedEmail: '',
    resetToken: '',
  })

  const forgotPassword = async (
    email: string
  ): Promise<ForgotPasswordResponse> => {
    const response = await apiClient.post<ForgotPasswordResponse>(
      '/auth/forgot-password',
      {
        email,
      } as ForgotPasswordRequest
    )
    return response.data
  }

  const handleSubmit = async (email: string): Promise<void> => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      isSuccess: false,
    }))

    try {
      const response = await forgotPassword(email)

      setState(prev => ({
        ...prev,
        data: response,
        isLoading: false,
        isSuccess: true,
        emailSent: true,
        submittedEmail: email,
        resetToken: response.reset_token,
      }))
    } catch (error) {
      const errorMessage = handleError(error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        isSuccess: false,
      }))
    }
  }

  const handleResend = async (): Promise<void> => {
    if (!state.submittedEmail) return

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }))

    try {
      const response = await forgotPassword(state.submittedEmail)

      setState(prev => ({
        ...prev,
        data: response,
        isLoading: false,
        resetToken: response.reset_token,
      }))

      toast({
        description: t('auth.emailSent'),
        variant: 'success',
        position: 'top-center',
      })
    } catch (error) {
      const errorMessage = handleError(error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
    }
  }

  const reset = () => {
    setState({
      data: null,
      isLoading: false,
      error: null,
      isSuccess: false,
      emailSent: false,
      submittedEmail: '',
      resetToken: '',
    })
  }

  return {
    ...state,
    handleSubmit,
    handleResend,
    reset,
  }
}
